import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { fetchResolvedAccessControl, ResolvedAccessControl } from "./AccessControlService";
import { ACCESS_CONTROL_MANIFEST } from "./manifest";

export type AccessControlMode = "permissive" | "strict";

interface AccessControlContextValue {
  loaded: boolean;
  isAdmin: boolean;
  userRoleIds: number[];
  mode: AccessControlMode;
  canViewPage: (pageKey: string, restrictedByDefault?: boolean) => boolean;
  canSeeElement: (pageKey: string, elementKey: string) => boolean;
  canAdminPage: (pageKey: string) => boolean;
  refresh: () => Promise<void>;
}

const DEFAULTS: AccessControlContextValue = {
  loaded: false,
  isAdmin: false,
  userRoleIds: [],
  mode: "permissive",
  canViewPage: () => true,
  canSeeElement: () => true,
  canAdminPage: () => false,
  refresh: async () => {},
};

const AccessControlContext = createContext<AccessControlContextValue>(DEFAULTS);

export const useAccessControl = () => useContext(AccessControlContext);

interface ProviderProps {
  children: ReactNode;
  mode?: AccessControlMode;
}

const REGISTERED_PAGE_KEYS = new Set(ACCESS_CONTROL_MANIFEST.map((p) => p.pageKey));

export const AccessControlProvider: React.FC<ProviderProps> = ({ children, mode = "permissive" }) => {
  const [state, setState] = useState<ResolvedAccessControl | null>(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchResolvedAccessControl();
      setState(data);
    } catch {
      setState(null);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const canViewPage = useCallback(
    (pageKey: string, restrictedByDefault = false) => {
      if (!state) return true;
      const p = state.pages[pageKey];
      // Explicit config exists → honour the resolved decision (Root + assigned
      // roles pass; everyone else is denied).
      if (p) return p.canView;
      // No config yet. A restricted-by-default page is visible only to Root
      // until Root assigns roles to it; then the branch above takes over.
      if (restrictedByDefault) return state.isAdmin;
      if (mode === "strict" && REGISTERED_PAGE_KEYS.has(pageKey)) {
        return false;
      }
      return true;
    },
    [state, mode],
  );

  const canSeeElement = useCallback(
    (pageKey: string, elementKey: string) => {
      if (!state) return true;
      const p = state.pages[pageKey];
      if (!p) return true;
      if (!(elementKey in p.elements)) return true;
      return p.elements[elementKey];
    },
    [state],
  );

  const canAdminPage = useCallback(
    (pageKey: string) => {
      if (!state) return false;
      if (state.isAdmin) return true;
      return !!state.pages[pageKey]?.canAdmin;
    },
    [state],
  );

  const value: AccessControlContextValue = {
    loaded,
    isAdmin: state?.isAdmin ?? false,
    userRoleIds: state?.userRoleIds ?? [],
    mode,
    canViewPage,
    canSeeElement,
    canAdminPage,
    refresh,
  };

  return <AccessControlContext.Provider value={value}>{children}</AccessControlContext.Provider>;
};
